import { EXTENSION_ICONS, EXTENSION_NAME_LEARN_WITH_AI_FRIENDLY, EXTENSION_NAME_LEARN_WITH_AI_OFFICIAL } from '@shared/extension/constants';
import { DataType } from '@shared/extension/enums/data-type.enum';
import { CURRENT_LANGUAGE_LOCAL_STORAGE_KEY } from '@shared/translate/constants';
import { Language } from '@shared/translate/enums';
import { filter, firstValueFrom, take } from 'rxjs';
import { AppView, StoreName } from '../../enums';
import { LocalStorageItem, ViewState } from '../../interfaces';
import { AbstractLocalStorageService } from '../../services/abstract/abstract-local-storage.service';
import { AbstractStore } from './abstract.store';

export abstract class AbstractViewStore extends AbstractStore<ViewState> {
  // Main selectors
  readonly authLoading$ = this.select('authLoading');
  readonly currencySign$ = this.select('currencySign');
  readonly currentLanguage$ = this.select('currentLanguage');
  readonly currentNotificationView$ = this.select('currentNotificationView');
  readonly currentView$ = this.select('currentView');
  readonly extensionIcon$ = this.select('extensionIcon');
  readonly extensionNameFriendly$ = this.select('extensionNameFriendly');
  readonly extensionNameOfficial$ = this.select('extensionNameOfficial');
  readonly notificationDetails$ = this.select('notificationDetails');
  readonly previousView$ = this.select('previousView');
  readonly translationsInitialized$ = this.select('translationsInitialized');

  constructor(protected override localStorageService: AbstractLocalStorageService) {
    super(localStorageService, StoreName.ViewStore, {
      authLoading: [false, DataType.Boolean],
      currencySign: ['$', DataType.String],
      currentLanguage: [Language.English, DataType.String],
      currentNotificationView: [null, DataType.String],
      currentView: [AppView.SignIn, DataType.String],
      extensionIcon: [EXTENSION_ICONS.LEARN_WITH_AI.DEFAULT.split('/').slice(1).join('/'), DataType.String],
      extensionNameFriendly: [EXTENSION_NAME_LEARN_WITH_AI_FRIENDLY, DataType.String],
      extensionNameOfficial: [EXTENSION_NAME_LEARN_WITH_AI_OFFICIAL, DataType.String],
      notificationDetails: [null, DataType.Object],
      previousView: [null, DataType.String],
      translationsInitialized: [false, DataType.Boolean],
    });
    this.getLocalStorage<Language>(CURRENT_LANGUAGE_LOCAL_STORAGE_KEY, DataType.String, false).then((currentLanguage: Language | undefined) => {
      if (currentLanguage) {
        this.updateStateProp('currentLanguage', currentLanguage);
      }
    });
    this.localStorageService.handleLocalStorageChanges().subscribe((item: LocalStorageItem) => {
      if (item.key === CURRENT_LANGUAGE_LOCAL_STORAGE_KEY) {
        const data = item.value as Language;
        if (this.state$.value.currentLanguage !== data) {
          this.updateStateProp('currentLanguage', data);
        }
      }
    });
  }

  async setAuthLoading(data: boolean): Promise<void> {
    await this.setValue('authLoading', data);
  }

  async setCurrencySign(data: string): Promise<void> {
    await this.setValue('currencySign', data);
  }

  async setCurrentLanguage(data: Language): Promise<void> {
    await this.setLocalStorage(CURRENT_LANGUAGE_LOCAL_STORAGE_KEY, data, false);
    this.updateStateProp('currentLanguage', data);
  }

  async setExtensionIcon(data: string): Promise<void> {
    await this.setValue('extensionIcon', data);
  }

  async setExtensionNameFriendly(data: string): Promise<void> {
    await this.setValue('extensionNameFriendly', data);
  }

  async setExtensionNameOfficial(data: string): Promise<void> {
    await this.setValue('extensionNameOfficial', data);
  }

  async setNotificationDetails(data: { title: string; message: string }): Promise<void> {
    await this.setValue('notificationDetails', data);
  }

  async setNotificationView(data: AppView | null): Promise<void> {
    const currentNotificationView: AppView | null = this.currentNotificationView$.value;
    if (!currentNotificationView && data) {
      await firstValueFrom(this.translationsInitialized$.pipe(filter((initialized: boolean) => initialized), take(1)));
    }
    this.updateStateProp('currentNotificationView', data);
  }

  async setView(data: AppView | null): Promise<void> {
    const currentView: AppView | null = this.currentView$.value;
    if (!currentView && data) {
      await firstValueFrom(this.translationsInitialized$.pipe(filter((initialized: boolean) => initialized), take(1)));
    }
    const state: ViewState = this.state$.value;
    await this.setValue('previousView', state.currentView);
    await this.setValue('currentView', data);
  }

  setTranslationsInitialized(data: boolean): void {
    this.updateStateProp('translationsInitialized', data);
  }
}
